import {
	CaseReducer,
	createAsyncThunk,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';
import { IThunkApiExtra, RootState } from '../store';
import { IPropertyCard } from '@/components/templates/search/interfaces';
import { fetchByCriteria } from './properties';

interface State {
	favorites: Record<number, boolean>;
}

export const initialState: State = {
	favorites: {},
};

const toggleFavoriteOptimisticReducer: CaseReducer<State, PayloadAction<number>> = (state, action) => {
	const idProperty = action.payload;
	state.favorites[idProperty] = !state.favorites[idProperty];
}

const setFavoriteReducer: CaseReducer<State, PayloadAction<{ idProperty: number; value: boolean }>> = (state, action) => {
	const { idProperty, value } = action.payload;
	state.favorites[idProperty] = value;
}

export const sendFavorite = createAsyncThunk<
	{ success: boolean },
	// idProperty es solo para el revert optimista local (favoritesState se indexa por idProperty)
	// — lo que se manda al backend es únicamente hashId, email y favorite (ver FavoriteRequest).
	{ idProperty?: number; hashId: string; email: string; favorite: boolean },
	{ extra: IThunkApiExtra }
>(
	'favorites/sendFavorite',
	async (request, thunkApi) => {
		const { rejectWithValue, extra } = thunkApi;
		const { services } = extra;
		const servicesList = (await services()).setupServices();
		const response = await servicesList.sendFavorite({ hashId: request.hashId, email: request.email, favorite: request.favorite });

		if (!response?.success) {
			return rejectWithValue({ idProperty: request.idProperty, previousValue: !request.favorite });
		}

		return { success: true };
	}
)

export const FavoritesSlice = createSlice({
	name: 'favorites',
	initialState,
	reducers: {
		toggleFavoriteOptimistic: toggleFavoriteOptimisticReducer,
		setFavorite: setFavoriteReducer,
	},
	extraReducers: builder => {
		builder.addCase(fetchByCriteria.fulfilled, (state, action) => {
			const results = (action.payload?.results ?? []) as IPropertyCard[];
			results.forEach((property) => {
				state.favorites[property.idProperty] = property.esFavorita ?? false;
			})
		})
		builder.addCase(sendFavorite.rejected, (state, action) => {
			const payload = action.payload as { idProperty?: number; previousValue: boolean } | undefined;
			if (payload?.idProperty !== undefined) state.favorites[payload.idProperty] = payload.previousValue;
		})
	}
})

export const { toggleFavoriteOptimistic, setFavorite } = FavoritesSlice.actions;

export const selectFavoritesState = (state: RootState) => state.FavoritesState.favorites;
export const selectIsFavorite = (idProperty: number) => (state: RootState) => !!state.FavoritesState.favorites[idProperty];
